import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faPlus, faEdit, faCheck, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './InternshipRequestForm.css';
import Footer from '../footer/Footer';
import Topbar from '../Topbar/Topbar';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const InternshipRequestForm = () => {
  const [areCompaniesAvailable, setAreCompaniesAvailable] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDataModalOpen, setDataModalOpen] = useState(false);
  const [isOtherModalOpen, setOtherModalOpen] = useState(false);
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [selectedData, setSelectedData] = useState([]);
  const [otherFormData, setOtherFormData] = useState({
    fullName: '',
    email: '',
    gender: '',
    level: '',
    phone: '',
    companyName: '',
    companyLocation: '',
    commencementDate: '',
    completionDate: '',
    modeOfCollection: ''
  });

  const [errorMessages, setErrorMessages] = useState({
    fullName: '',
    email: '',
    gender: '',
    level: '',
    phone: '',
    companyName: '',
    companyLocation: '',
    commencementDate: '',
    completionDate: '',
    modeOfCollection: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/organization/companies?type=${selectedCategory}`);
        if (response.ok) {
          const data = await response.json();
          const sortedData = data.sort((a, b) => a.companyName.localeCompare(b.companyName));
          setSelectedData(sortedData);
          setAreCompaniesAvailable(sortedData.length > 0);
        } else {
          console.error(`Error fetching data. Status: ${response.status}`);
          setAreCompaniesAvailable(false);
          toast.error('Failed to load companies. Please try again.');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setAreCompaniesAvailable(false);
        toast.error('Network error. Please check your connection and try again.');
      }
    };
    
    if (selectedCategory && selectedCategory !== 'Other') {
      fetchData();
    }
  }, [selectedCategory]);

  const handleCategoryClick = (category) => {
    if (category === 'Other') {
      setOtherModalOpen(true);
      setSelectedCategory('Other'); 
    } else {
      setSelectedCategory((prevCategory) => (prevCategory === category ? null : category));
      setOtherModalOpen(false);
      setDataModalOpen(false);
    }
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
    setDataModalOpen(true);
  };

  const closeModal = () => {
    setDataModalOpen(false);
    setOtherModalOpen(false);
    setConfirmationModalOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
  
    setOtherFormData({
      ...otherFormData,
      [name]: value,
    });
  
    setErrorMessages({
      ...errorMessages,
      [name]: '',
    });
  };

  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^(\+233|0)[0-9]{9}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };

  const validateForm = () => {
    const isOtherCategory = selectedCategory === 'Other';

    if (!otherFormData || typeof otherFormData !== 'object') {
      console.error('Form data is invalid.');
      toast.error('Form data is invalid. Please try again.');
      return false;
    }

    const requestData = {
      fullName: otherFormData.fullName || '',
      email: otherFormData.email || '',
      gender: (otherFormData.gender || '').toLowerCase(),
      classYear: otherFormData.level || '',
      phone: otherFormData.phone || '',
      companyName: isOtherCategory ? otherFormData.companyName || '' : selectedItem?.companyName || '',
      Location: isOtherCategory ? otherFormData.companyLocation || '' : selectedItem?.companyLocation || '',
      commencementDate: otherFormData.commencementDate || '',
      completionDate: otherFormData.completionDate || '',
      modeOfCollection: otherFormData.modeOfCollection || ''
    };

    const newErrorMessages = {
      fullName: '',
      gender: '',
      level: '',
      phone: '',
      companyName: '',
      companyLocation: '',
      commencementDate: '',
      completionDate: '',
      modeOfCollection: ''
    };

    if (!requestData.fullName) {
      newErrorMessages.fullName = 'Please enter your full name.';
    }

    if (!requestData.email) {
      newErrorMessages.email = 'Please enter your email address.';
    } else if (!/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(requestData.email)) {
      newErrorMessages.email = 'Please enter a valid email address.';
    }

    if (!requestData.gender) {
      newErrorMessages.gender = 'Please select your gender.';
    }

    if (!requestData.classYear) {
      newErrorMessages.level = 'Please select your level.';
    }

    if (!requestData.phone) {
      newErrorMessages.phone = 'Please enter your phone number.';
    } else if (!validatePhoneNumber(requestData.phone)) {
      newErrorMessages.phone = 'Please enter a valid Ghana phone number (e.g., 0551234567 or +233551234567).';
    }

    if (!requestData.companyName) {
      newErrorMessages.companyName = 'Please enter the company name.';
    }

    if (!requestData.Location) {
      newErrorMessages.companyLocation = 'Please enter the company location.';
    }

    if (!requestData.commencementDate) {
      newErrorMessages.commencementDate = 'Please enter the commencement date.';
    }

    if (!requestData.completionDate) {
      newErrorMessages.completionDate = 'Please enter the completion date.';
    }

    if (!requestData.modeOfCollection) {
      newErrorMessages.modeOfCollection = 'Select mode of collection.';
    }

    const today = new Date();
    const commencementDate = new Date(requestData.commencementDate);
    const completionDate = new Date(requestData.completionDate);

    if (requestData.commencementDate && commencementDate <= today) {
      newErrorMessages.commencementDate = "Commencement date must be in the future.";
    }

    if (requestData.commencementDate && requestData.completionDate) {
      const twoWeeksInMilliseconds = 14 * 24 * 60 * 60 * 1000;
      if (completionDate - commencementDate < twoWeeksInMilliseconds) {
        newErrorMessages.completionDate = 'The internship duration must be at least two weeks.';
      }
    }

    setErrorMessages(newErrorMessages);
    const isValid = !Object.values(newErrorMessages).some((message) => message !== '');
    
    if (!isValid) {
      toast.error('Kindly fix the highlighted errors before proceeding.');
    }
    
    return isValid;
  };

  const handleProceed = () => {
    if (validateForm()) {
      setConfirmationModalOpen(true);
      setDataModalOpen(false);
      setOtherModalOpen(false);
    }
  };

  const handleEdit = () => {
    setConfirmationModalOpen(false);
    if (selectedCategory === 'Other') {
      setOtherModalOpen(true);
    } else {
      setDataModalOpen(true);
    }
  };

const submitForm = async () => {
  if (isSubmitting) return;

  const isOtherCategory = selectedCategory === 'Other';


  const requestData = {
    fullName: otherFormData.fullName || '',
    email: otherFormData.email || '',
    gender: (otherFormData.gender || '').toLowerCase(),
    classYear: otherFormData.level || '',
    phone: (otherFormData.phone || '').replace(/\s/g, ''),
    companyName: isOtherCategory ? otherFormData.companyName || '' : selectedItem?.companyName || '',
    Location: isOtherCategory ? otherFormData.companyLocation || '' : selectedItem?.companyLocation || '',
    commencementDate: otherFormData.commencementDate,
    completionDate: otherFormData.completionDate,
    modeOfCollection: otherFormData.modeOfCollection || '',
    totalAmount: 12
  };

  setIsSubmitting(true);
  const submitToast = toast.loading('Submitting your internship request...');

  try {
    const response = await fetch(`${API_BASE_URL}/internship/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Server error details:', errorData);
      throw new Error(errorData.message || errorData.error || 'Failed to submit form');
    }

    const responseData = await response.json();
    console.log('Response data:', JSON.stringify(responseData, null, 2));

    // Extract paymentUrl from response
    const authorizationUrl = responseData.data?.paymentUrl;

    if (!authorizationUrl) {
      throw new Error(`No payment URL received. Response: ${JSON.stringify(responseData)}`);
    }

    window.location.href = authorizationUrl;

    toast.update(submitToast, {
      render: 'Redirecting to payment...',
      type: 'info',
      isLoading: false,
      autoClose: 3000,
    });

    setOtherFormData({
      fullName: '',
      gender: '',
      level: '',
      phone: '',
      companyName: '',
      companyLocation: '',
      commencementDate: '',
      completionDate: '',
      modeOfCollection: '',
    });

    setOtherModalOpen(false);
    setDataModalOpen(false);
    setConfirmationModalOpen(false);
    setSelectedCategory(null);
    setSelectedItem(null);
    setErrorMessages({});

  } catch (error) {
    console.error('Error:', error);

    toast.update(submitToast, {
      render: error.message || 'An error occurred. Please try again.',
      type: 'error',
      isLoading: false,
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  } finally {
    setIsSubmitting(false);
  }
};

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatPhone = (phone) => {
    if (!phone) return '';
    if (phone.startsWith('+233')) {
      return phone.replace('+233', '+233 ').replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3');
    } else if (phone.startsWith('0')) {
      return phone.replace(/(\d{3})(\d{3})(\d{4})/, '$1 $2 $3');
    }
    return phone;
  };

  return (
    <>
    <Topbar/>
    <div className='intern-div'>
      <div className='intern-main-container'>
        <h1>Intern Buddy</h1>
        <h4 className='msg-div'>Welcome to Intern Buddy! Are you ready to embark on a journey of exciting internship opportunities?</h4>
        
        <h2 className='msg-div-select'>Please select a category</h2>
        
        <div className='intern-div-form-card'>
          <div className={`accordion ${selectedCategory === 'Research' ? 'open' : ''}`}>
            <h2 onClick={() => handleCategoryClick('Research')}>Research</h2>
            {selectedCategory === 'Research' && (
              <ul>
                {areCompaniesAvailable ? (
                  selectedData.map((item) => (
                    <li key={item._id} onClick={() => handleItemClick(item)}>
                      <div className='li-flex'>
                        <div className='li-container'>
                          <p className='company-name'>
                            <strong>Institution:</strong> {item.companyName}
                          </p>
                          <div className='li-divider'>
                            <span>
                              <strong>Company Location:</strong> {item.companyLocation}
                            </span>
                            <span>
                              <strong>Intern Limit:</strong> {item.internLimit}
                            </span>
                          </div>
                        </div>
                        <span className=''>
                          <FontAwesomeIcon icon={faPlus} className='plus-xl'></FontAwesomeIcon>
                        </span>
                      </div>
                    </li>
                  ))
                ) : (
                  <li className='No-data'><FontAwesomeIcon icon={faInfoCircle} ></FontAwesomeIcon>No guaranteed slots are available for Research</li>
                )}
              </ul>
            )}
          </div>

          <div className={`accordion ${selectedCategory === 'Health' ? 'open' : ''}`}>
            <h2 onClick={() => handleCategoryClick('Health')}>Health</h2>
            {selectedCategory === 'Health' && (
              <ul>
                {areCompaniesAvailable ? (
                  selectedData.map((item) => (
                    <li key={item._id} onClick={() => handleItemClick(item)}>
                      <div className='li-flex'>
                        <div className='li-container'>
                          <span className='company-name'>
                            <strong>Institution:</strong> {item.companyName}
                          </span>
                          <div className='li-divider'>
                            <span>
                              <strong> Location:</strong> {item.companyLocation}
                            </span>
                            <span>
                              <strong>Intern Limit:</strong> {item.internLimit}
                            </span>
                          </div>
                          <span className='plus'>
                            <FontAwesomeIcon icon={faPlus} className='plus-xl'></FontAwesomeIcon>
                          </span>
                        </div>
                      </div>
                    </li>
                  ))
                ) : (
                  <li className='No-data'>  <FontAwesomeIcon icon={faInfoCircle} ></FontAwesomeIcon> No guaranteed slots are available for Health.</li>
                )}
              </ul>
            )}
          </div>

          <div className="other-category" onClick={() => handleCategoryClick('Other')}>
            <h2 className='other-accordion'>Other</h2>
          </div>
          
        </div>
        <div className='info-div'> <FontAwesomeIcon className='info-circle' icon={faInfoCircle} />Select the category <strong>other</strong>, if and only if the company you want to intern at is not found in either research or health.</div>

        {isDataModalOpen && (
          <div className="modal-o">
            <div className='icon-box'>
              <FontAwesomeIcon icon={faClose} className='close-button' onClick={closeModal}></FontAwesomeIcon>
            </div>
            <div className='form-flow'>
              <span className='center bold margin'>Company Information</span>

              <div className='company-info-div'>
                <p className='modal-company-name'>
                  <strong>Company Name:</strong> {selectedItem.companyName}
                </p>
                <p className='modal-company-location'>
                  <strong>Location:</strong> {selectedItem.companyLocation}
                </p>
              </div>
              <span className='center bold margin'>Personal Information</span>

              <label>
                <span className='label'>Full Name </span>
                <input
                  type="text"
                  name="fullName"
                  placeholder='John Doe'
                  value={otherFormData.fullName}
                  onChange={handleInputChange}
                />
                <span className="error-message">{errorMessages.fullName}</span>
              </label>

              <label>
                <span className='label'>Email Address</span>
                <input
                  type="email"
                  name="email"
                  placeholder='johndoe@example.com'
                  value={otherFormData.email}
                  onChange={handleInputChange}
                />
                <span className="error-message">{errorMessages.email}</span>
              </label>

              <label>
                <span className='label'> Gender</span>
                <select name="gender" value={otherFormData.gender} onChange={handleInputChange}>
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
                <span className="error-message">{errorMessages.gender}</span>
              </label>

              <label>
                <span className='label'> Level</span>
                <select name="level" value={otherFormData.level} onChange={handleInputChange}>
                  <option value="">Select Level</option>
                  <option value="200">200</option>
                  <option value="300">300</option>
                  <option value="400">400</option>
                </select>
                <span className="error-message">{errorMessages.level}</span>
              </label>

              <label>
                <span className='label'> Phone Number</span>
                <input
                  type="tel"
                  name="phone"
                  placeholder='0551234567 or +233551234567'
                  value={otherFormData.phone}
                  onChange={handleInputChange}
                />
                <span className="error-message">{errorMessages.phone}</span>
              </label>

              <label>
                <span className='label'> Commencement Date</span>
                <input
                  type="date"
                  name="commencementDate"
                  value={otherFormData.commencementDate}
                  onChange={handleInputChange}
                />
                <span className="error-message">{errorMessages.commencementDate}</span>
              </label>

              <label>
                <span className='label'>Completion Date</span>
                <input
                  type="date"
                  name="completionDate"
                  value={otherFormData.completionDate}
                  onChange={handleInputChange}
                />
                <span className="error-message">{errorMessages.completionDate}</span>
              </label>

              <label className="label">
                <span className='label'>Mode of Collection</span>
                <div className="radio-options">
                  <label>
                    <input
                      type="radio"
                      name="modeOfCollection"
                      value="Softcopy - WhatsApp"
                      checked={otherFormData.modeOfCollection === "Softcopy - WhatsApp"}
                      onChange={handleInputChange}
                    />
                    Softcopy - WhatsApp
                  </label>

                  <label>
                    <input
                      type="radio"
                      name="modeOfCollection"
                      value="Hardcopy - Pickup"
                      checked={otherFormData.modeOfCollection === "Hardcopy - Pickup"}
                      onChange={handleInputChange}
                    />
                    Hardcopy - Pickup
                  </label>
                </div>
                <span className="error-message">{errorMessages.modeOfCollection}</span>
              </label>
              

              <div className='btn'>
                <button type="button" onClick={handleProceed}>
                  Proceed
                </button>
              </div>
            </div>
          </div>
        )}

        {isOtherModalOpen && (
          <div className="modal-o">
            <div className='icon-box'>
              <FontAwesomeIcon icon={faClose} className='close-button' onClick={closeModal}></FontAwesomeIcon>
            </div>
            <div className='form-flow'>
              <h2>Complete the form below</h2>
              <label>
                <span className='label'>Full Name</span>
                <input
                  type="text"
                  name="fullName"
                  placeholder='John Doe'
                  value={otherFormData.fullName}
                  onChange={handleInputChange}
                />
                <span className="error-message">{errorMessages.fullName}</span>
              </label>

              <label>
                <span className='label'>Email Address</span>
                <input
                  type="email"
                  name="email"
                  placeholder='johndoe@example.com'
                  value={otherFormData.email}
                  onChange={handleInputChange}
                />
                <span className="error-message">{errorMessages.email}</span>
              </label>

              <label>
                <span className='label'>Gender</span>
                <select name="gender" value={otherFormData.gender} onChange={handleInputChange}>
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
                <span className="error-message">{errorMessages.gender}</span>
              </label>

              <label>
                <span className='label'>Level</span>
                <select name="level" value={otherFormData.level} onChange={handleInputChange}>
                  <option value="">Select Level</option>
                  <option value="200">200</option>
                  <option value="300">300</option>
                  <option value="400">400</option>
                </select>
                <span className="error-message">{errorMessages.level}</span>
              </label>

              <label>
                <span className='label'>Phone Number</span>
                <input
                  type="tel"
                  name="phone"
                  placeholder='0551234567 or +233551234567'
                  value={otherFormData.phone}
                  onChange={handleInputChange}
                />
                <span className="error-message">{errorMessages.phone}</span>
              </label>

              <label>
                <span className='label'>Company Name</span>
                <input
                  type="text"
                  name="companyName"
                  placeholder='Nestle Ghana Limited'
                  value={otherFormData.companyName}
                  onChange={handleInputChange}
                />
                <span className="error-message">{errorMessages.companyName}</span>
              </label>

              <label>
                <span className='label'>Company's Location</span>
                <input
                  type="text"
                  placeholder='Abelemkpe-Accra'
                  name="companyLocation"
                  value={otherFormData.companyLocation}
                  onChange={handleInputChange}
                />
                <span className="error-message">{errorMessages.companyLocation}</span>
              </label>

              <label>
                <span className='label'>Commencement Date</span>
                <input
                  type="date"
                  name="commencementDate"
                  value={otherFormData.commencementDate}
                  onChange={handleInputChange}
                />
                <span className="error-message">{errorMessages.commencementDate}</span>
              </label>

              <label>
                <span className='label'>Completion Date</span>
                <input
                  type="date"
                  name="completionDate"
                  value={otherFormData.completionDate}
                  onChange={handleInputChange}
                />
                <span className="error-message">{errorMessages.completionDate}</span>
              </label>

              <label className="label">
                <span className='label'>Mode of Collection</span>
                <div className="radio-options">
                  <label>
                    <div>
                    <input
                      type="radio"
                      name="modeOfCollection"
                      value="Softcopy - WhatsApp"
                      checked={otherFormData.modeOfCollection === "Softcopy - WhatsApp"}
                      onChange={handleInputChange}
                    />
                    </div>
                    <div>
                    Softcopy - WhatsApp
                    </div>
                  </label>

                  <label>
                    <div>
                    <input
                      type="radio"
                      name="modeOfCollection"
                      value="Hardcopy - Pickup"
                      checked={otherFormData.modeOfCollection === "Hardcopy - Pickup"}
                      onChange={handleInputChange}
                    />
                    </div>
                    <div>
                    Hardcopy - Pickup
                    </div>
                  </label>
                </div>
                <span className="error-message">{errorMessages.modeOfCollection}</span>
              </label>

              <div className='btn'>
                <button type="button" onClick={handleProceed}>
                  Proceed
                </button>
              </div>
            </div>
          </div>
        )}

        {isConfirmationModalOpen && (
          <div className="modal-o">
            <div className='icon-box'>
              <FontAwesomeIcon icon={faClose} className='close-button' onClick={closeModal}></FontAwesomeIcon>
            </div>
            <div className='form-flow'>
              <span className='center bold margin'>Confirm Your Details</span>
              <p className='center'>Kindly review your information before submitting</p>

              <div className='confirmation-details'>
                <div className='detail-row'>
                  <strong>Full Name:</strong> {otherFormData.fullName}
                </div>
                <div className='detail-row'>
                  <strong>Email:</strong> {otherFormData.email}
                </div>
                <div className='detail-row'>
                  <strong>Gender:</strong> {otherFormData.gender}
                </div>
                <div className='detail-row'>
                  <strong>Level:</strong> {otherFormData.level}
                </div>
                <div className='detail-row'>
                  <strong>Phone:</strong> {formatPhone(otherFormData.phone)}
                </div>
                <div className='detail-row'>
                  <strong>Company:</strong> {selectedCategory === 'Other' ? otherFormData.companyName : selectedItem?.companyName}
                </div>
                <div className='detail-row'>
                  <strong>Location:</strong> {selectedCategory === 'Other' ? otherFormData.companyLocation : selectedItem?.companyLocation}
                </div>
                <div className='detail-row'>
                  <strong>Start Date:</strong> {formatDate(otherFormData.commencementDate)}
                </div>
                <div className='detail-row'>
                  <strong>End Date:</strong> {formatDate(otherFormData.completionDate)}
                </div>
                <div className='detail-row'>
                  <strong>Mode of Collection:</strong> {otherFormData.modeOfCollection}
                </div>
              </div>

              <div className='btn-group'>
                <button type="button" onClick={handleEdit} className='edit-btn' disabled={isSubmitting}>
                  <FontAwesomeIcon icon={faEdit} /> Edit
                </button>
                <button 
                  type="button" 
                  onClick={submitForm} 
                  className='submit-btn green' 
                  disabled={isSubmitting}
                >
                  <FontAwesomeIcon icon={faCheck} /> 
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {(isDataModalOpen || isOtherModalOpen || isConfirmationModalOpen) && (
        <div className="modal-backdrop"></div>
    )}

    </div>
    <Footer/>
    
    <ToastContainer
      position="top-right"
      autoClose={5000}
      hideProgressBar={false}
      newestOnTop={false}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme="light"
    />
    </>
  );
};

export default InternshipRequestForm;